import { useCallback, useEffect, useMemo, useReducer, useRef, useState } from "react";
import { VideoPlaybackAction, VideoPlaybackState } from "../ProctoringResultView";

export default function useVideoControls(timeOffset: number = 0, initialHasError: boolean, initialDuration?: number) {
	const [duration, setDuration] = useState<number | undefined>(initialDuration);
	const [hasError, setHasError] = useState<boolean>(initialHasError);

	useEffect(() => {
		setHasError(initialHasError);
	}, [initialHasError]);

	useEffect(() => {
		setDuration(initialDuration);
	}, [initialDuration]);

	// Player control variables
	const [controlOptions, setControlOptions] = useState({
		isPlaying: false,
		playbackSpeed: 1,
		volume: 0.5,
	});

	const prevVolume = useRef<number>(controlOptions.volume);

	// Reducer function for useReducer React hook.
	// Handles 3 different dispatch cases
	const reducer = (state: VideoPlaybackState, action: VideoPlaybackAction) => {
		if (duration && !hasError) {
			switch (action.type) {
				case "play": {
					const now = performance.now();
					return {
						timer: state.timer + (now - state.lastTime) * action.speed,
						lastTime: now,
						skip: false,
					};
				}
				case "pause":
					return { timer: state.timer, lastTime: performance.now(), skip: false };
				case "skip":
					return {
						timer: Math.max(timeOffset, Math.min(Math.round(action.time), duration)),
						lastTime: performance.now(),
						skip: true,
					};
			}
		}
		return state;
	};

	// Global timer variables
	const [playback, playbackDispatch] = useReducer(reducer, { timer: 0, lastTime: performance.now(), skip: false });

	// In a ref so the useEffect doesn't rerun everytime
	const playbackTimerRef = useRef<number>(playback.timer);

	useEffect(() => {
		playbackTimerRef.current = playback.timer;
	}, [playback.timer]);

	useEffect(() => {
		let interval: NodeJS.Timeout;

		if (controlOptions.isPlaying && !hasError && duration) {
			interval = setInterval(() => {
				// Check if we reached the end
				if (playbackTimerRef.current >= duration) {
					setControlOptions((prev) => ({ ...prev, isPlaying: false }));
					playbackDispatch({ type: "pause" }); // Sync the lastTime
					return;
				}

				playbackDispatch({ type: "play", speed: controlOptions.playbackSpeed });
			}, 1000 / 60);
		}

		return () => {
			if (interval) clearInterval(interval);
		};
	}, [controlOptions.isPlaying, controlOptions.playbackSpeed, duration, hasError]);
	// Setting the video player to play/pause (code can probably be used to clean up the useEffect previously)
	const setPlay = () => {
		if (playbackTimerRef.current >= (duration ?? 0)) {
			playbackDispatch({ type: "skip", time: timeOffset });
		}

		// Used to reset the timer otherwise a paused video's time will continue to go up even when the video is paused (so unpausing it will cause a skip)
		// this will update the timer the second the video starts playing making sure that the calculations are correct (updating performance.now to the most recent time)
		playbackDispatch({ type: "pause" });
		setControlOptions((controlOptions) => ({
			...controlOptions,
			isPlaying: !controlOptions.isPlaying,
		}));
	};

	// Helps set/handle the volume changes of the video player
	const setVolume = (volume: number) => {
		prevVolume.current = volume;
		setControlOptions((controlOptions) => ({
			...controlOptions,
			volume: volume,
		}));
	};

	const toggleVolume = () => {
		setControlOptions((prevState) => {
			const isCurrentlyMuted = prevState.volume === 0;

			if (isCurrentlyMuted) {
				// UNMUTE: Restore the value we saved in the ref
				// Fallback to 0.5 if the ref was somehow 0
				return {
					...prevState,
					volume: prevVolume.current > 0 ? prevVolume.current : 0.5,
				};
			} else {
				// MUTE: Save the current volume before we set it to 0
				prevVolume.current = prevState.volume;
				return {
					...prevState,
					volume: 0,
				};
			}
		});
	};

	// Helps set/handle the playback speed changes of the video player
	const setSpeed = (value: string | number) => {
		const num = typeof value === "string" ? parseFloat(value) : value;
		setControlOptions((controlOptions) => ({
			...controlOptions,
			playbackSpeed: num,
		}));
	};

	const resetControls = useCallback(() => {
		setControlOptions((prev) => ({
			...prev,
			isPlaying: false,
			playbackSpeed: 1,
		}));
	}, []);

	const handleFullscreenKey = (setFullscreen: () => void) => {
		const focusedElement = document.activeElement;
		if (!focusedElement?.closest(".MuiModal-root")) {
			setFullscreen();
		}
	};

	const handleKeyboard = ({
		event,
		setFullscreen,
		ignoreKeyboardEvents,
		isErrorCase,
	}: {
		event: KeyboardEvent;
		setFullscreen: () => void;
		ignoreKeyboardEvents?: boolean;
		isErrorCase: boolean;
	}) => {
		if (ignoreKeyboardEvents) {
			return;
		}

		const el = document.activeElement as HTMLElement | null;
		const isTyping = el?.tagName === "INPUT" || el?.tagName === "TEXTAREA" || el?.isContentEditable;

		// If they are typing, we ignore all shortcuts
		if (isTyping) return;

		// if recording(s) has an error, only allow fullscreen shortcut
		if (isErrorCase) {
			if (event.key === "f" || event.key === "F") {
				handleFullscreenKey(setFullscreen);
			}
			return;
		}

		if (event.key === " ") {
			const tag = el?.tagName?.toLowerCase();
			const role = el?.getAttribute("role")?.toLowerCase();

			// Known interactive tags and ARIA roles
			const interactiveTags = ["button", "input", "select", "textarea", "a"];
			const interactiveRoles = [
				"button",
				"switch",
				"checkbox",
				"radio",
				"combobox",
				"menuitem",
				"option",
				"listbox",
				"tab",
				"slider",
				"option",
			];

			const isFocusable = el && el !== document.body;
			const isInteractiveTag = tag && interactiveTags.includes(tag);
			const isInteractiveRole = role && interactiveRoles.includes(role);

			if (!isFocusable || (!isInteractiveTag && !isInteractiveRole)) {
				event.preventDefault();
				setPlay();
			}
		} else if (event.key === "f" || event.key === "F") {
			handleFullscreenKey(setFullscreen);
		} else if (event.key === "ArrowRight") {
			const focusedElement = document.activeElement;
			if (
				(focusedElement?.tagName.toLowerCase() !== "input" &&
					focusedElement?.role !== "tab" &&
					focusedElement?.id !== "volume_button") ||
				focusedElement?.id === "seekbar_Video"
			) {
				playbackDispatch({ type: "skip", time: playbackTimerRef.current + 1000 });
			}
		} else if (event.key === "ArrowLeft") {
			const focusedElement = document.activeElement;
			if (
				(focusedElement?.tagName.toLowerCase() !== "input" &&
					focusedElement?.role !== "tab" &&
					focusedElement?.id !== "volume_button") ||
				focusedElement?.id === "seekbar_Video"
			) {
				playbackDispatch({ type: "skip", time: playbackTimerRef.current - 1000 });
			}
		} else if (event.key === "ArrowUp") {
			event.preventDefault();
			setControlOptions({ ...controlOptions, volume: Math.min(controlOptions.volume + 0.1, 1) });
		} else if (event.key === "ArrowDown") {
			event.preventDefault();
			setControlOptions({ ...controlOptions, volume: Math.max(controlOptions.volume - 0.1, 0) });
		} else if (event.key === "m" || event.key === "M") {
			toggleVolume();
		}
	};

	const getCurrentTime = () => playbackTimerRef.current;

	const changeDuration = (newDuration: number) => {
		setDuration(newDuration);
	};

	const hasFinishedPlaying = duration !== undefined && playbackTimerRef.current >= Math.floor(duration);

	const showOverlay = useMemo(() => !controlOptions.isPlaying, [controlOptions.isPlaying]);

	const toggleError = useCallback((error: boolean) => {
		setHasError(error);
	}, []);

	return {
		duration,
		changeDuration,
		resetControls,
		playbackDispatch,
		hasFinishedPlaying,
		showOverlay,
		getCurrentTime,
		handleKeyboard,
		setVolume,
		controlOptions,
		playback,
		setPlay,
		setSpeed,
		toggleVolume,
		hasError,
		toggleError,
	};
}
